import { Link, type LinkProps, useRouter } from "@tanstack/react-router"
import {
  CalendarOff,
  CalendarDays,
  ChevronDown,
  Compass,
  Eye,
  EyeOff,
  LayoutDashboard,
  Shield,
  Sparkles,
  Swords,
  type LucideIcon,
} from "lucide-react"
import { useEffect, useState } from "react"

import type { AuthUser } from "@/lib/auth-api"

import { Button } from "@/components/button"
import { adminNavSections as adminNavRegistry } from "@/lib/admin-nav"
import {
  useEffectiveIsAdmin,
  useViewAsMember,
  toggleAdminViewMode,
} from "@/lib/admin-view-store"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible"

interface NavLinkItem {
  kind: "link"
  to: LinkProps["to"]
  label: string
  icon: LucideIcon
}

interface NavSubmenuItem {
  kind: "submenu"
  label: string
  icon: LucideIcon
  items: NavLinkItem[]
}

type NavItem = NavLinkItem | NavSubmenuItem

interface NavSection {
  title: string
  items: NavItem[]
}

const sharedNavSections: NavSection[] = [
  {
    title: "Core",
    items: [
      { kind: "link", to: "/", label: "Dashboard", icon: LayoutDashboard },
      {
        kind: "link",
        to: "/guild-war-signup",
        label: "Guild War Signup",
        icon: Swords,
      },
      {
        kind: "link",
        to: "/guild-war-roster",
        label: "Guild War Roster",
        icon: Shield,
      },
      {
        kind: "link",
        to: "/events",
        label: "Guild Events",
        icon: CalendarDays,
      },
      {
        kind: "link",
        to: "/boss-talents",
        label: "Boss Talents",
        icon: Shield,
      },
      { kind: "link", to: "/absences", label: "Absences", icon: CalendarOff },
    ],
  },
]

const adminNavSections: NavSection[] = adminNavRegistry.map((section) => ({
  title: section.label,
  items: section.items.map((item) => {
    if (item.kind === "submenu") {
      return {
        kind: "submenu" as const,
        label: item.label,
        icon: item.icon,
        items: item.items.map((entry) => ({
          kind: "link" as const,
          to: entry.to,
          label: entry.label,
          icon: entry.icon,
        })),
      }
    }

    return {
      kind: "link" as const,
      to: item.to,
      label: item.label,
      icon: item.icon,
    }
  }),
}))

const isPathActive = (pathname: string, to: LinkProps["to"]) => {
  if (typeof to !== "string") {
    return false
  }

  if (to === "/") {
    return pathname === "/"
  }

  return pathname === to || pathname.startsWith(`${to}/`)
}

const getSubmenuKey = (sectionTitle: string, submenuLabel: string) =>
  `${sectionTitle}:${submenuLabel}`

const findActiveSubmenuKey = (sections: NavSection[], pathname: string) => {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.kind === "submenu") {
        const hasActiveItem = item.items.some((subItem) =>
          isPathActive(pathname, subItem.to),
        )

        if (hasActiveItem) {
          return getSubmenuKey(section.title, item.label)
        }
      }
    }
  }

  return null
}

const NavLink = ({
  to,
  label,
  icon: Icon,
  inset = false,
}: NavLinkItem & { inset?: boolean }) => {
  return (
    <Link
      to={to}
      className={`inline-flex w-full items-center gap-3 rounded-xl border border-transparent font-semibold text-foreground/82 transition-[background-color,border-color,color] duration-200 hover:bg-muted/55 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:outline-none data-[status=active]:border-border/60 data-[status=active]:bg-primary/10 data-[status=active]:text-foreground ${
        inset ? "px-3 py-2 text-[0.83rem]" : "px-3 py-2.5 text-sm"
      }`}
    >
      <Icon className="size-4" strokeWidth={2.1} />
      {label}
    </Link>
  )
}

const NavSubmenu = ({
  submenuKey,
  label,
  icon: Icon,
  items,
  isOpen,
  hasActiveItem,
  onOpenChange,
}: NavSubmenuItem & {
  submenuKey: string
  isOpen: boolean
  hasActiveItem: boolean
  onOpenChange: (submenuKey: string, nextOpen: boolean) => void
}) => {
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={(nextOpen) => {
        onOpenChange(submenuKey, nextOpen)
      }}
      className="border-l border-border/55 py-0.5 pl-2"
    >
      <CollapsibleTrigger className="text-kicker inline-flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-[0.76rem] tracking-[0.12em] text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:outline-none">
        <span
          className={`inline-flex items-center gap-2.5 ${
            hasActiveItem ? "text-foreground" : ""
          }`}
        >
          <Icon className="size-3.5" strokeWidth={2.1} />
          {label}
        </span>
        <ChevronDown
          className={`size-4 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-foreground/85" : "text-muted-foreground"
          }`}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-1 flex flex-col gap-0.5">
        {items.map((item) => (
          <NavLink key={item.to} {...item} inset />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

const SidebarNavSections = ({
  navSections,
  pathname,
  openSubmenuKey,
  onSubmenuChange,
}: {
  navSections: NavSection[]
  pathname: string
  openSubmenuKey: string | null
  onSubmenuChange: (submenuKey: string, nextOpen: boolean) => void
}) => {
  return (
    <div className="app-scrollbar flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain pr-1 md:gap-4">
      {navSections.map((section) => (
        <section
          key={section.title}
          className="grid gap-1 border-b border-border/40 pb-3 last:border-b-0 last:pb-0"
        >
          <h2 className="text-kicker px-3 text-[0.68rem] tracking-[0.16em] text-foreground/70">
            {section.title}
          </h2>
          <div className="flex flex-col gap-0.5">
            {section.items.map((item) => {
              if (item.kind === "link") {
                return <NavLink key={item.to} {...item} />
              }

              const submenuKey = getSubmenuKey(section.title, item.label)
              const hasActiveItem = item.items.some((subItem) =>
                isPathActive(pathname, subItem.to),
              )

              return (
                <NavSubmenu
                  key={submenuKey}
                  {...item}
                  submenuKey={submenuKey}
                  hasActiveItem={hasActiveItem}
                  isOpen={openSubmenuKey === submenuKey}
                  onOpenChange={onSubmenuChange}
                />
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

const MemberSpaceCard = () => {
  return (
    <section className="grid shrink-0 gap-2 border-t border-border/50 px-3 pt-3">
      <div className="text-kicker inline-flex items-center gap-2 text-[0.68rem] text-muted-foreground">
        <Sparkles className="size-3.5" />
        Member Space
      </div>
      <div className="flex items-start gap-2.5 text-sm/6 text-muted-foreground">
        <Compass
          className="mt-0.5 size-4 shrink-0 text-foreground/70"
          strokeWidth={2.1}
        />
        <p>
          Track your build, update boss clears, and stay ready whenever Holy
          Unicorn calls on you.
        </p>
      </div>
    </section>
  )
}

export const AppNavbar = ({ user }: { user: AuthUser }) => {
  const router = useRouter()
  const pathname = router.state.location.pathname
  const viewAsMember = useViewAsMember()
  const effectiveIsAdmin = useEffectiveIsAdmin(user.isAdmin)

  const navSections = effectiveIsAdmin
    ? [...sharedNavSections, ...adminNavSections]
    : sharedNavSections
  const activeSubmenuKey = findActiveSubmenuKey(navSections, pathname)
  const [openSubmenuKey, setOpenSubmenuKey] = useState<string | null>(
    activeSubmenuKey,
  )

  useEffect(() => {
    if (activeSubmenuKey) {
      setOpenSubmenuKey(activeSubmenuKey)
    }
  }, [activeSubmenuKey])

  return (
    <nav className="nav-surface flex h-full min-h-0 flex-col gap-3 md:gap-4">
      <div className="flex min-h-0 flex-1 flex-col">
        <SidebarNavSections
          navSections={navSections}
          pathname={pathname}
          openSubmenuKey={openSubmenuKey}
          onSubmenuChange={(nextSubmenuKey, nextOpen) => {
            setOpenSubmenuKey(() => (nextOpen ? nextSubmenuKey : null))
          }}
        />
      </div>

      {!user.isAdmin && <MemberSpaceCard />}

      {user.isAdmin && (
        <Button
          variant="ghost"
          size="sm"
          className={`h-auto w-full justify-start rounded-lg px-3 py-2 text-sm font-medium ${
            viewAsMember
              ? "bg-muted/65 text-foreground hover:bg-muted"
              : "text-muted-foreground hover:bg-muted/45 hover:text-foreground"
          }`}
          onClick={toggleAdminViewMode}
          aria-pressed={viewAsMember}
        >
          {viewAsMember ? (
            <>
              <EyeOff className="size-4" />
              Exit Member View
            </>
          ) : (
            <>
              <Eye className="size-4" />
              View as Member
            </>
          )}
        </Button>
      )}
    </nav>
  )
}
